import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Link2, ListChecks, Sparkles } from "lucide-react";
import FrothAiIcon from "../common/FrothAiIcon";
import { BetaBadge } from "../common/ui";

// A looping, simulated run of "Generate from Requirements": the testing
// expectation types itself in, criteria are picked one by one, FrothAI
// generates in the background, then scenarios and test cases stream in.
// The panel is aria-hidden — the heading and the guides above it describe
// the same flow in plain text, and reduced-motion visitors get the
// finished panel without the loop.

const REQUIREMENT = {
  id: "FSHOP-REQ-12",
  title: "Shopping cart and checkout",
  source: "Synced from Jira",
};

const PROMPT = "Focus on guest users, tax totals and saved addresses";
const CRITERIA = ["Functional", "Positive", "Negative", "Edge"];

// Mirrors the scenarios and test cases visible in the product screenshots.
const OUTPUT = [
  {
    id: "FSHOP-TS-3",
    tag: "Positive",
    title: "Add, update and remove cart items",
    cases: [
      { id: "FSHOP-TC-14", title: "Adding an item updates the cart badge and subtotal" },
      { id: "FSHOP-TC-15", title: "Changing quantity recalculates taxes and total" },
    ],
  },
  {
    id: "FSHOP-TS-4",
    tag: "Positive",
    title: "Guest and registered checkout",
    cases: [{ id: "FSHOP-TC-16", title: "Signed-in user can pick a saved address" }],
  },
  {
    id: "FSHOP-TS-5",
    tag: "Negative",
    title: "Checkout with missing or stale details",
    cases: [
      { id: "FSHOP-TC-17", title: "An expired card shows a recoverable error" },
      { id: "FSHOP-TC-18", title: "A price change after login recalculates the total" },
    ],
  },
];

// Flat reveal order: a scenario card, then its test cases, then the next scenario.
const ITEMS = [];
OUTPUT.forEach(scen => {
  ITEMS.push({ key: scen.id, kind: "scen" });
  scen.cases.forEach(item => ITEMS.push({ key: item.id, kind: "case" }));
});
const REVEAL_AT = {};
ITEMS.forEach((item, i) => {
  REVEAL_AT[item.key] = i;
});
const CASE_COUNT = OUTPUT.reduce((n, s) => n + s.cases.length, 0);

const TYPE_MS = 26; // per prompt character
const CHIP_MS = 150; // between criteria chips
const STAGGER_MS = 130; // between streamed results

export default function FrothAiDemo() {
  const [cycle, setCycle] = useState(0);
  const [typed, setTyped] = useState(0);
  const [chips, setChips] = useState(0);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [revealed, setRevealed] = useState(0);
  const [inView, setInView] = useState(false);
  const [reduced, setReduced] = useState(false);
  const rootRef = useRef(null);
  const playing = inView && !reduced;

  // The finished panel is the resting state for reduced motion and off-screen renders.
  const shown = {
    typed: playing ? typed : PROMPT.length,
    chips: playing ? chips : CRITERIA.length,
    busy: playing ? busy : false,
    progress: playing ? progress : 1,
    revealed: playing ? revealed : ITEMS.length,
  };

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Run the loop only while the demo is on screen.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.2 });
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // One pass of the demo; bumping `cycle` restarts the timeline.
  useEffect(() => {
    if (!playing) return undefined;
    const timers = [];
    const at = (ms, fn) => timers.push(setTimeout(fn, ms));

    at(0, () => {
      setTyped(0);
      setChips(0);
      setBusy(false);
      setProgress(0);
      setRevealed(0);
    });

    // The expectation types itself in.
    for (let i = 0; i < PROMPT.length; i++) at(250 + i * TYPE_MS, () => setTyped(i + 1));
    const typedEnd = 250 + PROMPT.length * TYPE_MS;

    // Criteria are picked one by one, then generation starts.
    CRITERIA.forEach((_, i) => at(typedEnd + 200 + i * CHIP_MS, () => setChips(i + 1)));
    const chipsEnd = typedEnd + 200 + CRITERIA.length * CHIP_MS;
    at(chipsEnd + 250, () => setBusy(true));
    for (let i = 0; i <= 16; i++) at(chipsEnd + 320 + i * 65, () => setProgress(i / 16));
    const genEnd = chipsEnd + 320 + 16 * 65;
    at(genEnd, () => setBusy(false));

    // Scenarios and test cases stream in.
    ITEMS.forEach((_, i) => at(genEnd + 200 + i * STAGGER_MS, () => setRevealed(i + 1)));

    // Hold the finished panel, then loop.
    timers.push(setTimeout(() => setCycle(c => c + 1), genEnd + 200 + ITEMS.length * STAGGER_MS + 2800));

    return () => timers.forEach(clearTimeout);
  }, [playing, cycle]);

  const done = shown.revealed >= ITEMS.length;

  return (
    <section className="ft-section fai-demo" ref={rootRef}>
      <div className="ft-container">
        <div className="fai-demo__head">
          <span className="ft-eyebrow">Live generation</span>
          <h2 className="ft-heading fai-demo__title">
            From one requirement to <span>ready-to-review tests</span>
          </h2>
          <p className="fai-demo__lead">
            FrothAI reads the requirement, proposes scenarios across positive, negative and edge cases, and
            drafts the test cases. You review, refine and insert the ones you want.
          </p>
        </div>
        <div className="fai-demo__panel" aria-hidden="true">
          <div className="fai-demo__top">
            <FrothAiIcon size={16} />
            Generate from requirements
            <BetaBadge />
            <span className="fai-demo__mode">
              <i />
              Demo
            </span>
          </div>
          <div className="fai-demo__grid">
            <div className="fai-demo__col">
              <div className="fai-demo__group">
                <span className="fai-demo__label">
                  <span className="fai-demo__num">01</span>
                  Requirement
                </span>
                <div className="fai-demo__req">
                  <span className="fai-demo__mono">{REQUIREMENT.id}</span>
                  <p>{REQUIREMENT.title}</p>
                  <span className="fai-demo__src">
                    <Link2 size={12} aria-hidden="true" /> {REQUIREMENT.source}
                  </span>
                </div>
              </div>
              <div className="fai-demo__group">
                <span className="fai-demo__label">Testing expectations</span>
                <p className="fai-demo__prompt">
                  {PROMPT.slice(0, shown.typed)}
                  <span className="fai-demo__caret" />
                </p>
              </div>
              <div className="fai-demo__group">
                <span className="fai-demo__label">Criteria</span>
                <div className="fai-demo__chips">
                  {CRITERIA.map((c, i) => (
                    <span key={c} className={`fai-demo__chip${i < shown.chips ? " is-in" : ""}`}>
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              <span className={`fai-demo__go${shown.busy ? " is-busy" : ""}`}>
                <FrothAiIcon size={15} />
                {shown.busy ? "Generating…" : "Generate with FrothAI"}
              </span>
            </div>
            <div className="fai-demo__col">
              <div className="fai-demo__group">
                <span className="fai-demo__label">
                  <span className="fai-demo__num">02</span>
                  FrothAI output
                </span>
                <div className={`fai-demo__status${done ? " is-done" : ""}`}>
                  {shown.busy ? (
                    <>
                      <span className="fai-demo__dots">
                        <i />
                        <i />
                        <i />
                      </span>
                      Generating in the background…
                    </>
                  ) : done ? (
                    <>
                      <CheckCircle2 size={15} className="fai-demo__ok" />
                      {`${OUTPUT.length} test scenarios · ${CASE_COUNT} test cases ready for review`}
                    </>
                  ) : (
                    <>
                      <Sparkles size={15} className="fai-demo__ok" />
                      Reading the requirement…
                    </>
                  )}
                </div>
                <div className="fai-demo__bar">
                  <i style={{ width: `${Math.round(shown.progress * 100)}%` }} />
                </div>
              </div>
              <div className="fai-demo__out">
                {OUTPUT.map(scen => (
                  <div
                    key={scen.id}
                    className={`fai-demo__scen fai-demo__item${REVEAL_AT[scen.id] < shown.revealed ? " is-in" : ""}`}
                  >
                    <div className="fai-demo__scen-head">
                      <span className="fai-demo__mono">{scen.id}</span>
                      <span className={`fai-demo__tag is-${scen.tag.toLowerCase()}`}>{scen.tag}</span>
                    </div>
                    <p>{scen.title}</p>
                    {scen.cases.map(item => (
                      <div
                        key={item.id}
                        className={`fai-demo__case fai-demo__item${REVEAL_AT[item.id] < shown.revealed ? " is-in" : ""}`}
                      >
                        <ListChecks size={13} aria-hidden="true" />
                        <span className="fai-demo__mono">{item.id}</span>
                        {item.title}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
